import { selectAdressInfos } from '@features/eligibility/eligibilitySlice';
import theme from '@theme';
import React from 'react';
import { BuildingFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import TitleSection from './TitleSection';
import { useSelector } from 'react-redux';
import { RootState } from 'src/app/store';
import { AddressInformationsSkeleton } from './skeltons';
import 'react-loading-skeleton/dist/skeleton.css';
import { LoaderIcon } from 'react-hot-toast';

import { useAppSelector } from '../../../../app/hooks';
import { BanCertificationType } from './enums';
import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';

const AddressInformations: React.FC = () => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const addressInfos = useAppSelector(selectAdressInfos);
  const { isSiteLoading, site } = useSelector((state: RootState) => state.site);

  if (!addressInfos) {
    return <AddressInformationsSkeleton />;
  }

  const { streetNr, streetName, postcode, city, housingUnitsNr } = addressInfos;

  const isCertified = site?.banCertification === BanCertificationType.CERTIFIED;

  return (
    <>
      <TitleSection>{t('buildingDetail.generalInfos', `General informations`)}</TitleSection>
      <CardInfos className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          {streetNr} {isSiteLoading ? <LoaderIcon /> : site?.buildingStreetNumberComplement},{' '}
          {streetName}, {postcode}, {city}
          {isSiteLoading ? (
            <LoaderIcon className="mx-2" />
          ) : (
            <CertificationTag isBan={isCertified}>
              {t(
                isCertified
                  ? 'buildingDetail.badges.banCertification.certified'
                  : 'buildingDetail.badges.banCertification.notCertified'
              )}
            </CertificationTag>
          )}
        </div>
        <CardTag className="d-flex align-items-center">
          <BuildingFill width={8} height={8} fill={theme.palette.blue[500]} />
          <div className="mx-2">
            {t('buildingDetail.numberOfHousings', 'Number of housings')}: {housingUnitsNr}
          </div>
        </CardTag>
      </CardInfos>
    </>
  );
};

const CardInfos = styled.div`
  font-size: ${theme.fonts.size.xs};
  font-weight: ${theme.fonts.weight.medium};
  color: ${theme.palette.blue[700]};
  background: ${theme.palette.blue[100]};
  padding: 0.4375rem 0.625rem;
  margin-top: 0.625rem;
  border-radius: 7px;
`;

const CardTag = styled.div`
  font-size: ${theme.fonts.size.xxs};
  background-color: ${theme.palette.blue[200]};
  padding: 0.125rem 0.25rem;
  border-radius: 5px;
`;

const CertificationTag = styled.div<{ isBan?: boolean }>`
  font-size: ${theme.fonts.size.xxs};
  font-weight: ${theme.fonts.weight.medium};
  line-height: 1.3;
  color: ${({ isBan }) => (isBan ? theme.palette.green[600] : theme.palette.red[600])};
  background-color: ${({ isBan }) => (isBan ? theme.palette.green[100] : theme.palette.red[100])};
  padding: 0.125rem 0.25rem;
  border-radius: 5px;
  margin-left: 1.5rem;
  position: relative;

  &::before {
    content: '';
    display: inline-block;
    position: absolute;
    left: -0.75rem;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 4px;
    background: ${theme.palette.grey[400]};
    border-radius: 50%;
  }
`;

export default AddressInformations;
